/**
 * ActivationChecklist — the second-stage flow shown on the portal home for
 * owner-clients whose `subscription_status` is not yet `active`. Three tiles:
 *
 *  1. Accept ongoing subscription terms (two inline mandatory checkboxes;
 *     both stay visible after acceptance so the client can see what they
 *     agreed to)
 *  2. Upload latest bank statement (PDF, for GST registration) — full-tile
 *     drag-and-drop with gold #C9A349 highlight on dragenter
 *  3. Pay first monthly fee — same card auto-debits every month after
 *
 * The Pay button is gated on 1 + 2. On success, Stripe redirects back to
 * /portal?activation_ok=1, the webhook flips the client to `active`, and
 * three back-office tasks land on info@nztaxation.nz's queue. When Stripe
 * fails, we surface an inline error with next-step guidance instead of a
 * silent 500.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, CircleDashed, FileUp, ClipboardSignature, CreditCard, Loader2, AlertTriangle } from "lucide-react";

const TILE_CX = "rounded-lg border border-[#E4E4E7] bg-white p-4 flex gap-3";
const DROP_ACTIVE_CX = "border-2 border-dashed border-[#C9A349] bg-[#FFFBEA]";

function StatusIcon({ done }) {
    return done ? (
        <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-white">
            <Check className="h-3.5 w-3.5" />
        </span>
    ) : (
        <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full border border-[#D4D4D8] text-[#B4B4BC]">
            <CircleDashed className="h-3.5 w-3.5" />
        </span>
    );
}

export default function ActivationChecklist() {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [termsBusy, setTermsBusy] = useState(false);
    const [feeAgreed, setFeeAgreed] = useState(false);
    const [yearAgreed, setYearAgreed] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [paying, setPaying] = useState(false);
    const [payError, setPayError] = useState(null);
    const fileRef = useRef(null);
    const dropRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/portal/activation/status");
            setState(data);
        } catch (e) {
            if (e?.response?.status === 404) setState(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        // Poll for 60s after a Stripe redirect so the state flips to "active" once webhook fires.
        const params = new URLSearchParams(window.location.search);
        if (params.get("activation_ok")) {
            const iv = setInterval(load, 2500);
            const stop = setTimeout(() => clearInterval(iv), 60000);
            return () => { clearInterval(iv); clearTimeout(stop); };
        }
        if (params.get("activation_cancelled")) {
            setPayError("Payment was cancelled. You can try again below when you're ready — nothing has been charged.");
        }
    }, [load]);

    const termsDone = !!state?.terms_accepted_at;
    const bankDone = !!state?.bank_statement_uploaded_at;
    const paidDone = state?.subscription_status === "active" || !!state?.first_payment_paid_at;
    const priceInclLine = state
        ? `NZ$${state.base_nzd?.toFixed(2)} + GST = NZ$${(state.amount_cents / 100).toFixed(2)} / month`
        : "";

    async function acceptTerms() {
        if (!feeAgreed || !yearAgreed) return;
        setTermsBusy(true);
        try {
            await api.post("/portal/activation/accept-terms", {
                fixed_fee_confirmed: feeAgreed, year_round_confirmed: yearAgreed,
            });
            toast.success("Terms accepted");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save terms");
        } finally {
            setTermsBusy(false);
        }
    }

    async function uploadPdf(file) {
        if (!file) return;
        if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
            toast.error("Please upload a PDF file.");
            return;
        }
        if (file.size > 20 * 1024 * 1024) {
            toast.error("That file is over 20 MB. Please compress or split it first.");
            return;
        }
        setUploading(true);
        try {
            const b64 = await new Promise((resolve, reject) => {
                const r = new FileReader();
                r.onload = () => resolve(String(r.result).split(",")[1] || "");
                r.onerror = reject;
                r.readAsDataURL(file);
            });
            await api.post("/portal/activation/upload-bank-statement", {
                filename: file.name,
                content_type: "application/pdf",
                data_base64: b64,
            });
            toast.success("Bank statement uploaded");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    }

    function onFilePicked(e) {
        const file = e.target.files?.[0];
        uploadPdf(file);
    }

    function onDragOver(e) { e.preventDefault(); e.stopPropagation(); if (!dragActive) setDragActive(true); }
    function onDragLeave(e) {
        e.preventDefault(); e.stopPropagation();
        // Only clear when leaving the drop tile itself (not a child)
        if (!dropRef.current?.contains(e.relatedTarget)) setDragActive(false);
    }
    function onDrop(e) {
        e.preventDefault(); e.stopPropagation();
        setDragActive(false);
        const file = e.dataTransfer?.files?.[0];
        uploadPdf(file);
    }

    async function startPayment() {
        setPaying(true);
        setPayError(null);
        try {
            const { data } = await api.post("/portal/activation/checkout", { origin_url: window.location.origin });
            // Break out of the Emergent preview iframe so Stripe Checkout
            // isn't refused via X-Frame-Options. `window.top` is only
            // cross-origin inside embedded previews — otherwise it is
            // the same window. Fall back to a new tab if the iframe
            // refuses top-navigation (rare, e.g. sandboxed with no
            // `allow-top-navigation`).
            try {
                if (window.top && window.top !== window.self) {
                    window.top.location.href = data.checkout_url;
                } else {
                    window.location.href = data.checkout_url;
                }
            } catch {
                window.open(data.checkout_url, "_blank", "noopener,noreferrer");
            }
        } catch (e) {
            const detail = e?.response?.data?.detail || "Could not start payment. Please try again in a minute.";
            setPayError(detail);
            toast.error(detail);
            setPaying(false);
        }
    }

    if (loading || !state) return null;
    if (state.subscription_status === "active") return null;

    return (
        <Card className="p-5" data-testid="activation-checklist">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#5E636E]">Activate your service</div>
                    <h2 className="text-xl font-semibold text-[#0F1115] mt-1">
                        {state.plan_label || "Your ClientOps plan"} · {priceInclLine}
                    </h2>
                    <p className="text-[13px] text-[#5E636E] mt-1">
                        Three quick steps to hand things over to NZ Taxation Limited t/a ClientOps.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Step 1 — inline terms with two mandatory ticks */}
                <div className={TILE_CX} data-testid="activation-tile-terms">
                    <StatusIcon done={termsDone} />
                    <div className="flex-1">
                        <div className="flex items-center gap-2 text-[13px] font-semibold text-[#0F1115]">
                            <ClipboardSignature className="h-4 w-4" /> 1. Accept ongoing terms
                        </div>
                        <div className="mt-3 space-y-2.5 text-[12.5px] text-[#1F2328]">
                            <label className="flex items-start gap-2">
                                <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={termsDone || feeAgreed}
                                    onChange={(e) => setFeeAgreed(e.target.checked)}
                                    disabled={termsDone}
                                    data-testid="activation-terms-fee"
                                />
                                <span>I understand the monthly fee is fixed at <b>NZ$75 + GST</b> and will be auto-debited.</span>
                            </label>
                            <label className="flex items-start gap-2">
                                <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={termsDone || yearAgreed}
                                    onChange={(e) => setYearAgreed(e.target.checked)}
                                    disabled={termsDone}
                                    data-testid="activation-terms-year"
                                />
                                <span>I understand my tax obligations run <b>year-round</b> and I must supply records on time.</span>
                            </label>
                        </div>
                        <Button
                            size="sm"
                            variant={termsDone ? "outline" : "default"}
                            className="mt-3"
                            disabled={termsDone || !feeAgreed || !yearAgreed || termsBusy}
                            onClick={acceptTerms}
                            data-testid="activation-open-terms"
                        >
                            {termsBusy ? (<><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Saving…</>) : termsDone ? "Accepted" : "Accept"}
                        </Button>
                    </div>
                </div>

                {/* Step 2 — drag-and-drop bank statement */}
                <div
                    ref={dropRef}
                    className={(dragActive ? DROP_ACTIVE_CX : TILE_CX) + " transition-colors"}
                    onDragEnter={onDragOver}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    data-testid="activation-tile-bank"
                >
                    <StatusIcon done={bankDone} />
                    <div className="flex-1">
                        <div className="flex items-center gap-2 text-[13px] font-semibold text-[#0F1115]">
                            <FileUp className="h-4 w-4" /> 2. Upload bank statement
                        </div>
                        <div className="text-[12px] text-[#5E636E] mt-1">
                            {bankDone ? "PDF received — thanks." : (
                                <>Latest PDF of your business bank account. <b className="text-[#0F1115]">Drag & drop it here</b> or use the button below.</>
                            )}
                        </div>
                        {bankDone && state.bank_statement_filename && (
                            <div className="mt-2 flex items-center gap-2 text-[12.5px]">
                                <FileUp className="h-3.5 w-3.5 text-[#5E636E]" />
                                <span className="text-[#0F1115] truncate max-w-[220px]" title={state.bank_statement_filename} data-testid="activation-bank-filename">
                                    {state.bank_statement_filename}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    className="text-[#5E636E] hover:text-[#0F1115] underline text-[11px]"
                                    data-testid="activation-bank-replace"
                                >
                                    Replace
                                </button>
                            </div>
                        )}
                        <input
                            type="file"
                            accept="application/pdf,.pdf"
                            className="hidden"
                            ref={fileRef}
                            onChange={onFilePicked}
                            data-testid="activation-bank-file-input"
                        />
                        {!bankDone && (
                            <Button
                                size="sm"
                                variant="default"
                                className="mt-3"
                                disabled={uploading}
                                onClick={() => fileRef.current?.click()}
                                data-testid="activation-upload-bank"
                            >
                                {uploading ? (<><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Uploading…</>) : "Upload PDF"}
                            </Button>
                        )}
                        {dragActive && !bankDone && (
                            <div className="mt-2 text-[11px] font-medium text-[#8A6A00]" data-testid="activation-bank-drop-hint">
                                Release to upload your PDF
                            </div>
                        )}
                    </div>
                </div>

                {/* Step 3 — pay first fee */}
                <div className={TILE_CX} data-testid="activation-tile-pay">
                    <StatusIcon done={paidDone} />
                    <div className="flex-1">
                        <div className="flex items-center gap-2 text-[13px] font-semibold text-[#0F1115]">
                            <CreditCard className="h-4 w-4" /> 3. Pay your first fee
                        </div>
                        <div className="text-[12px] text-[#5E636E] mt-1">
                            Same card is saved for auto-debit every month after — cancel any time in writing.
                        </div>
                        <Button
                            size="sm"
                            className="mt-3"
                            disabled={!termsDone || !bankDone || paying || paidDone}
                            onClick={startPayment}
                            data-testid="activation-pay-now"
                        >
                            {paying
                                ? (<><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Opening secure payment…</>)
                                : paidDone
                                    ? "Paid"
                                    : `Pay NZ$${(state.amount_cents / 100).toFixed(2)} →`}
                        </Button>
                        {payError && (
                            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[12px] text-[#78511A]" role="alert" data-testid="activation-pay-error">
                                <div className="flex items-start gap-1.5">
                                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                    <div>
                                        <div className="font-medium mb-0.5">Payment didn&apos;t start</div>
                                        <div>{payError}</div>
                                        <button
                                            type="button"
                                            onClick={startPayment}
                                            className="mt-1.5 underline hover:text-[#0F1115]"
                                            data-testid="activation-pay-retry"
                                        >
                                            Try again
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {(!termsDone || !bankDone) && (
                <div className="mt-3 text-[11px] text-[#5E636E]" data-testid="activation-gating-note">
                    Complete step 1 and step 2 to unlock payment.
                </div>
            )}
        </Card>
    );
}
